import { Button } from "@frontend/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@frontend/components/ui/popover"
import { defaultSliceLayouts, SLICE_TYPE } from "@niivue/nvreact"
import { Check, LayoutGrid } from "lucide-react"

export const sliceTypeOptions: Record<string, { label: string; sliceType: number }> = {
  "slice:axial": { label: "Axial", sliceType: SLICE_TYPE.AXIAL },
  "slice:coronal": { label: "Coronal", sliceType: SLICE_TYPE.CORONAL },
  "slice:sagittal": { label: "Sagittal", sliceType: SLICE_TYPE.SAGITTAL },
  "slice:multiplanar": { label: "Multiplanar", sliceType: SLICE_TYPE.MULTIPLANAR },
  "slice:render": { label: "3D Render", sliceType: SLICE_TYPE.RENDER }
}

const sliceLayoutLabelOverrides: Record<string, string> = {
  "quad-render": "Ortho + Render"
}

interface ViewerSliceLayoutPickerProps {
  currentLayoutKey: string | null
  onLayoutChange: (layoutKey: string | null) => void
}

function PickerItem({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={`flex w-full items-center justify-between rounded px-2 py-1 text-xs hover:bg-muted ${
        selected ? "font-medium" : ""
      }`}
      onClick={onClick}
    >
      {label}
      {selected ? <Check className="size-3" /> : null}
    </button>
  )
}

export function ViewerSliceLayoutPicker({ currentLayoutKey, onLayoutChange }: ViewerSliceLayoutPickerProps) {
  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            size="icon"
            variant="ghost"
            className="pointer-events-auto size-5 bg-black/50 text-white/70 hover:bg-black/70 hover:text-white"
            aria-label="Slice layout"
            title="Slice layout"
          />
        }
      >
        <LayoutGrid className="size-2.5" />
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" className="w-44 gap-0 p-1">
        <PickerItem label="Default" selected={currentLayoutKey === null} onClick={() => onLayoutChange(null)} />
        <div className="my-1 h-px bg-border" />
        <p className="px-2 py-1 text-[10px] font-medium text-muted-foreground">Slice View</p>
        {Object.entries(sliceTypeOptions).map(([key, config]) => (
          <PickerItem
            key={key}
            label={config.label}
            selected={currentLayoutKey === key}
            onClick={() => onLayoutChange(key)}
          />
        ))}
        <div className="my-1 h-px bg-border" />
        <p className="px-2 py-1 text-[10px] font-medium text-muted-foreground">Multi-Panel Layout</p>
        {Object.entries(defaultSliceLayouts).map(([key, config]) => (
          <PickerItem
            key={key}
            label={sliceLayoutLabelOverrides[key] ?? config.label}
            selected={currentLayoutKey === key}
            onClick={() => onLayoutChange(key)}
          />
        ))}
      </PopoverContent>
    </Popover>
  )
}
